import { LAYOUT_KEY, isLayoutShapeValid } from './dock-layout';
import { LAYER_ORDER_PREFIX, isValidStoredOrder } from './layer-order-store';
import { STYLE_DEFAULTS_KEY, isDefaultsShapeValid } from './style-defaults';

/**
 * Versioned migrations over the localStorage keys the app writes.
 * Rule: any change to a stored shape ships with a migration here.
 *
 * Keys written today (informational):
 *   ultimate-gis-theme          'light' | 'dark' | 'system'   (ThemeContext)
 *   ribbon-display              'both'|'icons'|... (GisRibbon, Viewer3DRibbon)
 *   ultimate-gis-theme also holds 'system' (Settings); the old 'theme' key is dropped by migration 6
 *   <org storage key>           active org id      (OrgContext)
 *   <mapping key by headers>    column mappings    (ColumnMappingDialog)
 *   ultimate-gis:update-snooze  snoozed build id   (lib/updateToast)
 *   ultimate-gis:layout:v1      dock layout JSON   (hooks/useDockLayout, lib/dock-layout)
 *   ultimate-gis:layer-order:v1:<project>  layer draw order, JSON array (hooks/useMapLayers, lib/layer-order-store)
 *   ultimate-gis:style-defaults:v1  per-geometry-type default styles (lib/style-defaults, properties panel)
 *   supabase auth session       owned by supabase-js, never migrate
 * sessionStorage: viewer3d-focus, ultimate-gis:map-view (ephemeral, no migration).
 */
export const STATE_VERSION_KEY = 'ultimate-gis:state-version';

export const MIGRATIONS = [
  {
    version: 1,
    name: 'record baseline version',
    up() { /* no-op: establishes the version marker */ },
  },
  {
    version: 2,
    name: 'drop an invalid dock layout (ultimate-gis:layout:v1)',
    up(store) {
      const raw = store.getItem(LAYOUT_KEY);
      if (raw === null || raw === undefined) return;
      let ok = false;
      try { ok = isLayoutShapeValid(JSON.parse(raw)); } catch { ok = false; }
      if (!ok) store.removeItem(LAYOUT_KEY);
    },
  },
  {
    version: 3,
    name: 'rename the dock panel style to properties in a stored layout (Wave 5.3)',
    up(store) {
      const raw = store.getItem(LAYOUT_KEY);
      if (raw === null || raw === undefined) return;
      let layout;
      try { layout = JSON.parse(raw); } catch { return; }
      if (!isLayoutShapeValid(layout)) return;
      const rename = (id) => (id === 'style' ? 'properties' : id);
      Object.keys(layout.tabs).forEach((d) => { layout.tabs[d] = layout.tabs[d].map(rename); });
      if (layout.active && typeof layout.active === 'object') {
        Object.keys(layout.active).forEach((d) => { layout.active[d] = rename(layout.active[d]); });
      }
      if (layout.floating && layout.floating.style) {
        layout.floating.properties = layout.floating.style;
        delete layout.floating.style;
      }
      store.setItem(LAYOUT_KEY, JSON.stringify(layout));
    },
  },
  {
    version: 4,
    name: 'drop invalid saved default styles (ultimate-gis:style-defaults:v1)',
    up(store) {
      const raw = store.getItem(STYLE_DEFAULTS_KEY);
      if (raw === null || raw === undefined) return;
      let ok = false;
      try { ok = isDefaultsShapeValid(JSON.parse(raw)); } catch { ok = false; }
      if (!ok) store.removeItem(STYLE_DEFAULTS_KEY);
    },
  },
  {
    version: 5,
    name: 'drop invalid per-project layer orders (ultimate-gis:layer-order:v1:*)',
    up(store) {
      if (typeof store.key !== 'function') return; // minimal storage without enumeration
      const bad = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (!k || !k.startsWith(LAYER_ORDER_PREFIX)) continue;
        let ok = false;
        try { ok = isValidStoredOrder(JSON.parse(store.getItem(k))); } catch { ok = false; }
        if (!ok) bad.push(k);
      }
      bad.forEach((k) => store.removeItem(k));
    },
  },
  {
    version: 6,
    name: 'drop the old ThemeSwitcher key (theme); ThemeContext owns ultimate-gis-theme',
    up(store) {
      const old = store.getItem('theme');
      if (old === null || old === undefined) return;
      // Carry an explicit choice over unless the new key is already set.
      if (['light', 'dark', 'system'].includes(old) && store.getItem('ultimate-gis-theme') === null) {
        store.setItem('ultimate-gis-theme', old);
      }
      store.removeItem('theme');
    },
  },
];

export const CURRENT_STATE_VERSION = MIGRATIONS[MIGRATIONS.length - 1].version;

export function runStateMigrations(storage, migrations = MIGRATIONS) {
  let store = storage;
  if (!store) {
    try { store = window.localStorage; } catch { return 0; }
  }
  let current = 0;
  try { current = parseInt(store.getItem(STATE_VERSION_KEY), 10) || 0; } catch { return 0; }
  for (const m of migrations) {
    if (m.version <= current) continue;
    try {
      m.up(store);
      current = m.version;
      store.setItem(STATE_VERSION_KEY, String(current));
    } catch (err) {
      console.warn(`State migration v${m.version} failed`, err);
      break;
    }
  }
  return current;
}
